import {
  Directive,
  inject,
  Input,
  OnInit,
  Provider,
  signal,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { PERMISSION_STATUS } from '../../models';
import { PermissionType } from '../../models/permission.data';
import { PermissionsService } from '../../services/permissions.service';

@Directive({
  selector: '[qtPermission]',
  standalone: true,
  providers: [
    // provider added to the directive's injector, returning the instance signal
    {
      provide: PERMISSION_STATUS,
      useFactory: (dir: PermissionDirective) => dir.permissionStatus,
      deps: [PermissionDirective],
    } as Provider,
  ],
})
export class PermissionDirective implements OnInit {
  private router = inject(Router);
  private templateRef = inject(TemplateRef<unknown>);
  private viewContainer = inject(ViewContainerRef);
  private permissionsService = inject(PermissionsService);
  private permissionStatus = signal<string | undefined>(undefined);

  @Input('qtPermission') permissionType:
    | PermissionType
    | PermissionType[]
    | string = PermissionType.Add;
  @Input('qtPermissionFeature') feature: string | string[];

  ngOnInit(): void {
    this.checkPermission();
  }

  private checkPermission(): void {
    const url = this.feature || this.router.url;
    const validatedPermission = this.validatePermissionTypes(
      this.permissionType
    );

    this.permissionsService
      .checkPermission$(url, validatedPermission)
      .pipe(take(1))
      .subscribe(({ isAllowed, status }) => {
        this.permissionStatus.set(status);
        if (isAllowed) {
          this.viewContainer.createEmbeddedView(this.templateRef);
        } else {
          this.viewContainer.clear();
        }
      });
  }

  private validatePermissionTypes(
    permissions: PermissionType | PermissionType[] | string
  ): PermissionType[] {
    if (Array.isArray(permissions)) {
      return permissions.map(permission =>
        this.validatePermissionType(permission)
      );
    }
    return [this.validatePermissionType(permissions)];
  }

  private validatePermissionType(
    permission: PermissionType | string
  ): PermissionType {
    if (Object.values(PermissionType).includes(permission as PermissionType)) {
      return permission as PermissionType;
    }

    return PermissionType.Add;
  }
}
